import { getApiUrl } from '@/lib/api-config';

export interface DashboardData {
  // KPI Cards
  totalOutstanding: number;
  paidThisMonth: number;
  overdueInvoices: number;
  draftInvoices: number;
  
  // Outstanding invoices by amount
  outstandingByAmount: Array<{ project: string; amount: number; percentage: number }>;
  remainingExposureByProject: Array<{ project: string; invoiced: number; budget: number; remaining: number }>;
  
  // Open invoices by project
  openInvoicesByProject: Array<{ project: string; count: number; percentage: number }>;
  
  // Upcoming payments (not fully paid, not void, has due date)
  upcomingPayments: Array<{ invoice: string; project: string; amount: number; daysUntilPayment: number }>;
  
  // Cash collected by month (last 6 months)
  cashCollected: Array<{ month: string; amount: number }>;
  
  // AR Aging
  arAging: Array<{ range: string; amount: number; count: number }>;
  
  // Top clients
  topClients: {
    fastestPaying: Array<{ client: string; avgDays: number }>;
    slowestPaying: Array<{ client: string; avgDays: number }>;
    highestPaid: Array<{ client: string; amount: number }>;
    largestOutstanding: Array<{ client: string; amount: number }>;
  };
  
  // Invoice status breakdown
  invoiceStatus: Array<{ status: string; count: number; percentage: number }>;
}

const localToday = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

/**
 * The Invoices dashboard. Every figure is computed by the backend (GET /api/invoices/dashboard) from the same rows and rules as the Invoices / Payments tables,
 * in ONE request that reads only the columns the figures need. `today` is the user's own calendar day: "overdue" and "this month" are theirs, not the server's.
 * (It used to read every invoice, payment and project straight from the browser and work the figures out here.)
 */
export async function fetchInvoicesDashboard(projectId: string | undefined, signal?: AbortSignal): Promise<DashboardData> {
  const params = new URLSearchParams({ today: localToday() });
  if (projectId && projectId !== 'all') params.set('project_id', projectId);
  const response = await fetch(getApiUrl(`api/invoices/dashboard?${params}`), { signal });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body?.error || body?.message || 'The dashboard could not be loaded');
  return body.data as DashboardData;
}
